import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, Mail, Phone, Building, Calendar, 
  ExternalLink, Check, X, Clock, MessageSquare,
  TrendingUp, Filter, Download, RefreshCw, Trash2,
  ChevronDown, Search, LogOut, LayoutDashboard
} from 'lucide-react';
import { isAuthenticated, logout } from './AdminLogin';

// =============================================================================
// COLORS
// =============================================================================

const c = {
  bg: '#0a0a0a',
  bgCard: 'rgba(255, 255, 255, 0.03)',
  bgHover: 'rgba(255, 255, 255, 0.05)',
  text: '#ffffff',
  textMuted: 'rgba(255, 255, 255, 0.7)',
  textSubtle: 'rgba(255, 255, 255, 0.5)',
  border: 'rgba(255, 255, 255, 0.08)',
  accent: '#007AFF',
  success: '#22c55e',
  warning: '#f59e0b',
  error: '#ef4444',
};

const STATUS_CONFIG = {
  new: { label: 'Ny', color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)' },
  contacted: { label: 'Kontaktet', color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.1)' },
  meeting_booked: { label: 'Møte booket', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)' },
  proposal_sent: { label: 'Tilbud sendt', color: '#06b6d4', bg: 'rgba(6, 182, 212, 0.1)' },
  won: { label: 'Vunnet', color: '#22c55e', bg: 'rgba(34, 197, 94, 0.1)' },
  lost: { label: 'Tapt', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)' },
};

// =============================================================================
// STATS CARD
// =============================================================================

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div style={{
      background: c.bgCard,
      border: `1px solid ${c.border}`,
      borderRadius: '12px',
      padding: '20px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: '12px', color: c.textSubtle, marginBottom: '6px' }}>{label}</div>
          <div style={{ fontSize: '28px', fontWeight: '300', color: c.text }}>{value}</div>
        </div>
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '10px',
          background: color ? `${color}15` : c.bgCard,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Icon size={20} color={color || c.textMuted} />
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// LEAD ROW
// =============================================================================

function LeadRow({ lead, onStatusChange, onDelete, isSelected, onSelect }) {
  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState(lead.notes || '');
  const [saving, setSaving] = useState(false);
  
  const status = STATUS_CONFIG[lead.status] || STATUS_CONFIG.new;
  const createdAt = new Date(lead.created_at);
  
  const handleStatusChange = async (newStatus) => {
    setSaving(true);
    await onStatusChange(lead.id, newStatus, notes);
    setSaving(false);
  };
  
  const handleSaveNotes = async () => {
    setSaving(true);
    await onStatusChange(lead.id, lead.status, notes);
    setSaving(false);
    setShowNotes(false);
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: isSelected ? c.bgHover : c.bgCard,
        border: `1px solid ${isSelected ? 'rgba(255,255,255,0.15)' : c.border}`,
        borderRadius: '12px',
        marginBottom: '12px',
        overflow: 'hidden',
      }}
    >
      <div style={{
        padding: '16px 20px',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr auto auto',
        gap: '16px',
        alignItems: 'center',
      }}>
        {/* Lead Info */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
            <span style={{ fontSize: '15px', fontWeight: '500', color: c.text }}>{lead.name}</span>
            {lead.company && (
              <span style={{ fontSize: '12px', color: c.textSubtle }}>@ {lead.company}</span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <a href={`mailto:${lead.email}`} style={{ 
              display: 'flex', alignItems: 'center', gap: '4px',
              fontSize: '13px', color: c.textMuted, textDecoration: 'none',
            }}>
              <Mail size={12} /> {lead.email}
            </a>
            {lead.phone && (
              <a href={`tel:${lead.phone}`} style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                fontSize: '13px', color: c.textMuted, textDecoration: 'none',
              }}>
                <Phone size={12} /> {lead.phone}
              </a>
            )}
          </div>
        </div>
        
        {/* Source & Time */}
        <div>
          <div style={{ fontSize: '13px', color: c.textMuted, marginBottom: '4px' }}>
            {lead.utm_source || 'Direkte'} 
            {lead.utm_campaign && ` · ${lead.utm_campaign}`}
          </div>
          <div style={{ fontSize: '12px', color: c.textSubtle }}>
            {createdAt.toLocaleDateString('nb-NO')} kl {createdAt.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
        
        {/* Status Dropdown */}
        <div style={{ position: 'relative' }}>
          <select
            value={lead.status}
            onChange={(e) => handleStatusChange(e.target.value)}
            disabled={saving}
            style={{
              appearance: 'none',
              background: status.bg,
              border: `1px solid ${status.color}40`,
              borderRadius: '8px',
              padding: '8px 32px 8px 12px',
              fontSize: '12px',
              fontWeight: '500',
              color: status.color,
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            {Object.entries(STATUS_CONFIG).map(([key, config]) => (
              <option key={key} value={key}>{config.label}</option>
            ))}
          </select>
          <ChevronDown size={14} style={{
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: status.color,
            pointerEvents: 'none',
          }} />
        </div>
        
        {/* Actions */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => setShowNotes(!showNotes)}
            style={{
              background: showNotes ? 'rgba(255,255,255,0.1)' : 'transparent',
              border: `1px solid ${c.border}`,
              borderRadius: '8px',
              padding: '8px',
              cursor: 'pointer',
              color: c.textMuted,
            }}
            title="Notater"
          >
            <MessageSquare size={16} />
          </button>
          <button
            onClick={() => onDelete(lead.id)}
            style={{
              background: 'transparent',
              border: `1px solid ${c.border}`,
              borderRadius: '8px',
              padding: '8px',
              cursor: 'pointer',
              color: c.error,
            }}
            title="Slett"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
      
      {/* Message & Notes */}
      <AnimatePresence>
        {(showNotes || lead.message) && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            style={{
              borderTop: `1px solid ${c.border}`,
              padding: '16px 20px',
              background: 'rgba(0,0,0,0.2)',
            }}
          >
            {lead.message && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '11px', color: c.textSubtle, marginBottom: '6px' }}>MELDING</div>
                <p style={{ fontSize: '13px', color: c.textMuted, margin: 0, lineHeight: 1.6 }}>
                  {lead.message}
                </p>
              </div>
            )}
            
            {showNotes && (
              <div>
                <div style={{ fontSize: '11px', color: c.textSubtle, marginBottom: '6px' }}>NOTATER</div>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Legg til notater..."
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '13px',
                    background: 'rgba(255,255,255,0.03)',
                    border: `1px solid ${c.border}`,
                    borderRadius: '8px',
                    color: c.text,
                    resize: 'vertical',
                    minHeight: '80px',
                    outline: 'none',
                    fontFamily: 'inherit',
                  }}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                  <button
                    onClick={handleSaveNotes}
                    disabled={saving}
                    style={{
                      background: c.accent,
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: '#fff',
                      cursor: 'pointer',
                      opacity: saving ? 0.7 : 1,
                    }}
                  >
                    {saving ? 'Lagrer...' : 'Lagre'}
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// =============================================================================
// MAIN ADMIN LEADS COMPONENT
// =============================================================================

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [sources, setSources] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', source: '', search: '' });
  
  const navigate = useNavigate();
  
  // Check authentication
  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/admin', { state: { from: { pathname: '/admin/leads' } } });
    }
  }, [navigate]);
  
  const handleLogout = () => {
    logout();
    navigate('/admin');
  };
  
  const fetchLeads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.status) params.append('status', filter.status);
      if (filter.source) params.append('source', filter.source);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/leads?${params}`);
      const text = await response.text();
      const data = JSON.parse(text);
      
      setLeads(data.leads || []);
      setStats(data.stats || {});
      setSources(data.sources || {});
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchLeads();
  }, [filter.status, filter.source]);
  
  const handleStatusChange = async (leadId, status, notes) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/leads/${leadId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, notes }),
      });
      fetchLeads();
    } catch (error) {
      console.error('Error updating lead:', error);
    }
  };
  
  const handleDelete = async (leadId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne leaden?')) return;
    
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/leads/${leadId}`, {
        method: 'DELETE',
      });
      fetchLeads();
    } catch (error) {
      console.error('Error deleting lead:', error);
    }
  };
  
  const exportCSV = () => {
    const headers = ['Navn', 'E-post', 'Telefon', 'Selskap', 'Status', 'Kilde', 'Kampanje', 'Dato', 'Melding'];
    const rows = leads.map(lead => [
      lead.name,
      lead.email,
      lead.phone || '',
      lead.company || '',
      STATUS_CONFIG[lead.status]?.label || lead.status,
      lead.utm_source || 'Direkte',
      lead.utm_campaign || '',
      new Date(lead.created_at).toLocaleDateString('nb-NO'),
      lead.message || '',
    ]);
    
    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `leads-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };
  
  // Filter leads by search
  const filteredLeads = leads.filter(lead => {
    if (!filter.search) return true;
    const search = filter.search.toLowerCase();
    return (
      lead.name?.toLowerCase().includes(search) ||
      lead.email?.toLowerCase().includes(search) ||
      lead.company?.toLowerCase().includes(search)
    );
  });
  
  return (
    <div style={{
      minHeight: '100vh',
      background: c.bg,
      color: c.text,
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@200;300;400;500;600&display=swap');
        * { box-sizing: border-box; }
        ::selection { background: rgba(255,255,255,0.2); }
        select option { background: #1a1a1a; color: #fff; }
      `}</style>
      
      {/* Header */}
      <header style={{
        borderBottom: `1px solid ${c.border}`,
        padding: '16px 32px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <span style={{ fontSize: '28px', fontWeight: '100', color: c.text }}>a.</span>
          </Link>
          <div>
            <h1 style={{ fontSize: '18px', fontWeight: '500', marginBottom: '2px', color: c.text }}>Leads</h1>
            <p style={{ fontSize: '12px', color: c.textSubtle, margin: 0 }}>CRM Dashboard</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button
            onClick={fetchLeads}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: 'transparent',
              border: `1px solid ${c.border}`,
              borderRadius: '8px',
              padding: '10px 16px',
              fontSize: '13px',
              color: c.textMuted,
              cursor: 'pointer',
            }}
          >
            <RefreshCw size={14} /> Oppdater
          </button>
          <button
            onClick={exportCSV}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: c.accent,
              border: 'none',
              borderRadius: '8px',
              padding: '10px 16px',
              fontSize: '13px',
              fontWeight: '500',
              color: '#fff',
              cursor: 'pointer',
            }}
          >
            <Download size={14} /> Eksporter
          </button>
          <button
            onClick={handleLogout}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: 'transparent',
              border: `1px solid ${c.border}`,
              borderRadius: '8px',
              padding: '10px 14px',
              fontSize: '13px',
              color: c.textMuted,
              cursor: 'pointer',
            }}
            title="Logg ut"
          >
            <LogOut size={14} />
          </button>
        </div>
      </header>
      
      <main style={{ padding: '32px' }}>
        {/* Stats Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '16px',
          marginBottom: '32px',
        }}>
          <StatCard label="Totalt" value={stats.total || 0} icon={Users} color={c.accent} />
          <StatCard label="Nye" value={stats.new || 0} icon={Clock} color="#3b82f6" />
          <StatCard label="Kontaktet" value={stats.contacted || 0} icon={Mail} color="#8b5cf6" />
          <StatCard label="Møter" value={stats.meeting_booked || 0} icon={Calendar} color="#f59e0b" />
          <StatCard label="Vunnet" value={stats.won || 0} icon={Check} color="#22c55e" />
        </div>
        
        {/* Filters */}
        <div style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '24px',
          flexWrap: 'wrap',
        }}>
          <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
            <Search size={16} style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: c.textSubtle,
            }} />
            <input
              type="text"
              placeholder="Søk etter navn, e-post eller selskap..."
              value={filter.search}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
              style={{
                width: '100%',
                padding: '10px 12px 10px 40px',
                fontSize: '13px',
                background: c.bgCard,
                border: `1px solid ${c.border}`,
                borderRadius: '8px',
                color: c.text,
                outline: 'none',
              }}
            />
          </div>
          
          <select
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            style={{
              padding: '10px 32px 10px 12px',
              fontSize: '13px',
              background: c.bgCard,
              border: `1px solid ${c.border}`,
              borderRadius: '8px',
              color: c.text,
              cursor: 'pointer',
              outline: 'none',
              appearance: 'none',
            }}
          >
            <option value="">Alle statuser</option>
            {Object.entries(STATUS_CONFIG).map(([key, config]) => (
              <option key={key} value={key}>{config.label}</option>
            ))}
          </select>
          
          <select
            value={filter.source}
            onChange={(e) => setFilter({ ...filter, source: e.target.value })}
            style={{
              padding: '10px 32px 10px 12px',
              fontSize: '13px',
              background: c.bgCard,
              border: `1px solid ${c.border}`,
              borderRadius: '8px',
              color: c.text,
              cursor: 'pointer',
              outline: 'none',
              appearance: 'none',
            }}
          >
            <option value="">Alle kilder</option>
            {Object.entries(sources).map(([source, count]) => (
              <option key={source} value={source}>{source} ({count})</option>
            ))}
          </select>
        </div>
        
        {/* Leads List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: c.textSubtle }}>
            Laster leads...
          </div>
        ) : filteredLeads.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '60px',
            background: c.bgCard,
            borderRadius: '12px',
            border: `1px solid ${c.border}`,
          }}>
            <Users size={40} color={c.textSubtle} style={{ marginBottom: '16px' }} />
            <h3 style={{ fontSize: '16px', color: c.text, marginBottom: '8px' }}>Ingen leads ennå</h3>
            <p style={{ fontSize: '14px', color: c.textSubtle }}>
              Leads fra kampanjesidene vil dukke opp her.
            </p>
          </div>
        ) : (
          <div>
            {filteredLeads.map(lead => (
              <LeadRow
                key={lead.id}
                lead={lead}
                onStatusChange={handleStatusChange}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
